import useModalState from "@logics/hooks/useModalState";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import ConfirmModal from "@domains/common/views/modal/ConfirmModal/ConfirmModal";

import {
  useDeleteCommentMutation,
  useUpdateCommentMutation,
} from "../network/eventMutations";
import CommentForm from "../views/components/CommentForm/CommentForm";
import CommentMenuBottomSheet from "../views/components/CommentMenuBottomSheet/CommentMenuBottomSheet";
import { COMMENT_MESSAGE } from "../constants/toastMessages";
import CommentOtherMenuBottomSheet from "../views/components/CommentOtherMenuBottomSheet/CommentOtherMenuBottomSheet";

interface UseCommentMenuProps {
  isMySelf: boolean;
  eventId?: string;
  commentId?: number;
  payload?: string;
}

/**
 * commentCard에 사용되는 햄버거 메뉴의 항목에 해당하는 기능들이 있는 Hook
 */
export const useCommentMenu = ({
  isMySelf,
  eventId,
  commentId,
  payload,
}: UseCommentMenuProps) => {
  const { openModal, closeModal } = useModalState();

  const commentUpdateMutation = useUpdateCommentMutation();
  const commentDeleteMutation = useDeleteCommentMutation();

  // COMMENT UPDATE
  const handleUpdateComment = async (text: string) => {
    if (!eventId || !commentId) return;

    try {
      await commentUpdateMutation.mutateAsync({
        eventId,
        commentId,
        text,
      });

      ToastSuccess(COMMENT_MESSAGE.COMMENT_EDITED);

      closeModal();
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    }
  };

  const handleClickEditComment = () => {
    openModal({
      CustomComponent: (
        <CommentForm
          headerTitle="Edit comment"
          payload={payload}
          onSubmitComment={(text) => handleUpdateComment(text)}
        />
      ),
    });
  };

  // COMMENT DELETE
  const handleDeleteComment = async () => {
    if (!eventId || !commentId) return;

    try {
      await commentDeleteMutation.mutateAsync({
        eventId,
        commentId,
      });

      ToastSuccess(COMMENT_MESSAGE.COMMENT_DELETED);

      closeModal();
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    }
  };

  const handleClickDeleteComment = () => {
    openModal({
      CustomComponent: (
        <ConfirmModal
          modalTitle="Confirm Comment Deletion"
          modalDescription="Are you sure you want to delete your comment?"
          cancelButton={{
            onClickCancel: closeModal,
          }}
          confirmButton={{
            buttonText: "Delete",
            onClickConfirm: handleDeleteComment,
          }}
        />
      ),
    });
  };

  // NOTE: 내가 쓴 COMMENT에 대한 MENU 오픈
  const handleClickCommenterMenu = () => {
    openModal({
      CustomComponent: (
        <CommentMenuBottomSheet
          commentType="COMMENT"
          onClickEdit={handleClickEditComment}
          onClickDelete={handleClickDeleteComment}
        />
      ),
    });
  };

  // NOTE: 다른 유저가 쓴 COMMENT에 대한 MENU 오픈
  const handleClickOtherMenu = () => {
    if (!commentId) return;

    openModal({
      CustomComponent: (
        <CommentOtherMenuBottomSheet
          commentType="COMMENT"
          targetId={commentId}
        />
      ),
    });
  };

  return {
    handleClickMenu: isMySelf ? handleClickCommenterMenu : handleClickOtherMenu,
  };
};
